/**
 * Лента событий: что случилось и когда.
 *
 * Матрица показывает СОСТОЯНИЕ, лента — СОБЫТИЯ. По одному состоянию нельзя
 * отличить «так было всегда» от «сломалось сегодня ночью», а это разные
 * поводы к действию: первое обсуждают, второе чинят.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import type { EventItem, EventsPage } from '../../shared/protocol/events.ts';
import type { EventLevel } from '../../shared/domain/status.ts';

const FILTERS: { id: string; label: string; level?: EventLevel }[] = [
  { id: 'all', label: 'Всё' },
  { id: 'error', label: 'Сбои', level: 'error' },
  { id: 'warn', label: 'Предупреждения', level: 'warn' },
  { id: 'info', label: 'Обычное', level: 'info' },
];

/** Машинный тип события словами. Неизвестный показываем как есть. */
const TYPE_LABELS: Record<string, string> = {
  'mssql.chain-broken': 'цепочка журналов',
  'mssql.job': 'задание SQL',
  'mssql.split': 'задвоение источника',
  'collector.failed': 'сборщик',
  'collector.mount': 'шара',
  'pve.no-jobs': 'Proxmox',
  'pve.tls': 'сертификат',
  'inventory.seeded': 'инвентарь',
  'auth.login': 'вход',
};

const LEVEL_GLYPH: Record<EventLevel, string> = { info: '·', warn: '▲', error: '✕' };

function when(iso: string): string {
  const d = new Date(iso);
  const hours = (Date.now() - d.getTime()) / 3600000;
  const time = d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  if (hours < 1) return `${time} · только что`;
  if (hours < 24) return `${time} · ${Math.round(hours)} ч назад`;
  return `${time} · ${Math.round(hours / 24)} сут назад`;
}

function Row({ item }: { item: EventItem }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const hasData = item.data && Object.keys(item.data).length > 0;

  return (
    <li className={`feed__item feed__item--${item.level}`}>
      <div className="feed__head" onClick={() => hasData && setOpen((v) => !v)}>
        <span className="feed__glyph" aria-hidden="true">{LEVEL_GLYPH[item.level]}</span>
        <span className="feed__when">{when(item.ts)}</span>
        <span className="feed__type">{TYPE_LABELS[item.type] ?? item.type}</span>
        {item.sourceKey && <span className="feed__source">{item.sourceKey}</span>}
      </div>
      <p className="feed__msg">{item.msg}</p>
      {open && hasData && (
        <pre className="feed__data mono">{JSON.stringify(item.data, null, 2)}</pre>
      )}
    </li>
  );
}

export function Events(): React.JSX.Element {
  const [filter, setFilter] = useState('all');
  const active = FILTERS.find((f) => f.id === filter) ?? FILTERS[0]!;
  const { data, error, loading } = useQuery<EventsPage>('events.list', {
    ...(active.level ? { level: active.level } : {}),
    limit: 80,
  });

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">События</h2>
        <p className="muted">
          Что случилось и когда. Матрица отвечает на вопрос «как сейчас», лента — «что
          изменилось»; по состоянию не отличить давнюю беду от сегодняшней.
        </p>
      </header>

      <nav className="subtabs" aria-label="Уровень">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`subtabs__item${filter === f.id ? ' subtabs__item--active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </nav>

      <div className="panel__scroll">
        {error && <p className="alert alert--error">Ошибка: {error.msg}</p>}
        {!error && !data && <p className="muted">{loading ? 'Загружаю…' : 'Нет данных.'}</p>}
        {data && data.items.length === 0 && (
          <p className="muted">Пусто. На этом уровне ничего не записано.</p>
        )}
        {data && data.items.length > 0 && (
          <ul className="feed">
            {data.items.map((item, i) => (
              <Row key={`${item.ts}-${i}`} item={item} />
            ))}
          </ul>
        )}
      </div>

      <footer className="panel__foot">
        <span>Лента только дописывается: ничто в коде её не правит и не чистит.</span>
        {data && <span className="panel__stamp">показано {data.items.length}</span>}
      </footer>
    </section>
  );
}
